import { useDispatch, useSelector } from "react-redux";

import {
  AlternateIcon,
  DatasetWidgetIcon,
  ExpandCollapseIcon,
  MapRecenterIcon,
  MinusIcon,
  PlusIcon,
  PolygonIcon,
  ZoomToFitIcon
} from "@/assets/icons";
import AnalysisJobListMapMenuControl from "@/components/spatialAnalysis/analysisJobList/analysisJobListControl";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import {
  setAreaInput,
  setPolygonSettingsTemp
} from "@/store/slice/spatialSlice";
import { enableDrawPolygon } from "@/store/slice/uiSlice";
import { AreaInputType } from "@/types/spatial";

import { twMerge } from "tailwind-merge";

import MapMenuItemControl from "../menuItemControl";
import BaseMapMenu from "./BaseMapMenu";

type Props = {
  zoomLevel: number;
  isLegendVisible: boolean;
  handleMapFullScreen: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onToggleLegendVisibility: () => void;
  onToggleLegendVisualization: () => void;
  onToggleAnalysisJobList: () => void;
  onToggleDatasetWidget: () => void;
  isDrawPolygonEnabled?: boolean;
  isDatasetWidgetVisible?: boolean;
};

const MapMenuControl = ({
  zoomLevel,
  isLegendVisible,
  handleMapFullScreen,
  onZoomIn,
  onZoomOut,
  onToggleLegendVisibility,
  onToggleLegendVisualization,
  onToggleAnalysisJobList,
  onToggleDatasetWidget,
  isDatasetWidgetVisible
}: Props) => {
  const dispatch = useDispatch();
  const { enterDrawMode, enterSelectMode, synchronizeFeatures, resetMapView } =
    useMap();
  const { isDrawPolygon, showAnalysisResultPopup, showAnalysisSummaryPopup } =
    useSelector((state: RootState) => state.ui);
  const { polygonSettingsTemp, polygonSettings, latestAnalysisId } =
    useSelector((state: RootState) => state.spatial);
  const {
    isDrawPolygonEnabled,
    isAnalysisJobListEnabled,
    isBaseMapStyleSelectorEnabled,
    isDatasetWidgetEnabled
  } = useAppFeatures();

  const onOpenDrawPolygon = () => {
    if (isDrawPolygon) return;

    const data = {
      value: AreaInputType.POLYGON,
      label: "Polygon"
    };

    dispatch(setAreaInput(data));
    dispatch(enableDrawPolygon());
    synchronizeFeatures();
    dispatch(setPolygonSettingsTemp(polygonSettings));

    if (Object.keys(polygonSettingsTemp || {}).length < 3) {
      enterDrawMode();
    } else {
      enterSelectMode();
    }
  };

  return (
    <>
      {/* Top right menu */}
      {isAnalysisJobListEnabled && (
        <AnalysisJobListMapMenuControl
          onToggleAnalysisJobList={onToggleAnalysisJobList}
        />
      )}
      {isDatasetWidgetEnabled && !latestAnalysisId && (
        <MapMenuItemControl
          className={twMerge(
            "absolute top-20 right-6 z-[99] rounded-lg border-[1px] border-gainsboro",
            isDatasetWidgetVisible && "border-teal-500"
          )}
          onClick={onToggleDatasetWidget}
          aria-label="Widget Summary"
          tooltipContent="Widget Summary"
        >
          <DatasetWidgetIcon
            className={twMerge(
              "fill-dim-gray",
              isDatasetWidgetVisible && "fill-teal-500"
            )}
          />
        </MapMenuItemControl>
      )}
      {/* Bottom right menu */}
      <div
        className={twMerge(
          "absolute bottom-[24px] right-[24px] z-[99] flex flex-col",
          "items-center"
        )}
        data-testid="bottom-right-controls"
      >
        <MapMenuItemControl
          className="rounded-lg border-[1px] border-gainsboro mb-2"
          onClick={resetMapView}
          aria-label="Recenter Map"
          tooltipContent="Recenter map position."
        >
          <MapRecenterIcon className="fill-dim-gray cursor-pointer" />
        </MapMenuItemControl>
        {isDrawPolygonEnabled &&
          !showAnalysisResultPopup &&
          !showAnalysisSummaryPopup && (
            <MapMenuItemControl
              className={twMerge(
                "rounded-lg border-[1px] border-gainsboro my-2",
                isDrawPolygon && "border-teal-500"
              )}
              onClick={onOpenDrawPolygon}
              aria-label="Draw Polygon"
              tooltipContent="Draw a custom polygon on the map."
            >
              <PolygonIcon
                className={twMerge(
                  "fill-dim-gray",
                  isDrawPolygon && "fill-teal-500"
                )}
              />
            </MapMenuItemControl>
          )}
        <MapMenuItemControl
          className={twMerge(
            "rounded-lg border-[1px] border-gainsboro my-2",
            !isLegendVisible && "hidden"
          )}
          onClick={onToggleLegendVisualization}
          tooltipContent="Switch to dual map view."
        >
          <ExpandCollapseIcon className="fill-dim-gray" />
        </MapMenuItemControl>
        <MapMenuItemControl
          className="rounded-lg border-[1px] border-gainsboro"
          onClick={handleMapFullScreen}
          tooltipContent="View in full screen."
        >
          <ZoomToFitIcon className="fill-dim-gray" />
        </MapMenuItemControl>
        <div className="my-2 rounded-lg border-[1px] border-gainsboro overflow-hidden">
          <MapMenuItemControl onClick={onZoomIn}>
            <PlusIcon className="fill-dim-gray" />
          </MapMenuItemControl>
          <MapMenuItemControl>
            <span className="text-txtBody select-none font-700 text-sm">
              {Math.floor(zoomLevel)}
            </span>
          </MapMenuItemControl>
          <MapMenuItemControl onClick={onZoomOut}>
            <MinusIcon className="fill-dim-gray" />
          </MapMenuItemControl>
        </div>
        <MapMenuItemControl
          className={cn(
            "rounded-lg border-[1px] border-gainsboro",
            isLegendVisible && "border-teal-500"
          )}
          onClick={onToggleLegendVisibility}
        >
          <AlternateIcon className="fill-dim-gray" />
        </MapMenuItemControl>

        {isBaseMapStyleSelectorEnabled && <BaseMapMenu />}
      </div>
    </>
  );
};

export default MapMenuControl;
